import { MoneyCurrency, type ChatStoreProductInput } from '__generated__/commerce';
import type { ChatOrderProduct } from '../ChatOrderProductCandidatesModal';
import type { SelectionItem, ProductDeliveryState, SelectionState } from './chatOrderReducer';

const money = (amount: number, currency: MoneyCurrency = MoneyCurrency.Won) => ({ amount, currency });

const getDeliveryFeeInput = (product: ChatOrderProduct, delivery?: ProductDeliveryState) => {
  const amount = delivery?.includeDeliveryFee
    ? (product.deliveryFeeOptions[delivery.selectedDeliveryFeeOptionIndex]?.fee.amount ?? 0)
    : 0;

  return amount > 0 ? money(amount) : null;
};

const getOptionsInput = (product: ChatOrderProduct, optionItems: SelectionItem[]) => {
  if (optionItems.length === 0) return null;

  const result = optionItems
    .map((item) => {
      const option = product.options.find((o) => o._id === item.optionId);
      return option ? { name: option.name, price: money(item.price), quantity: item.quantity } : null;
    })
    .filter((o): o is NonNullable<typeof o> => o !== null);

  return result.length > 0 ? result : null;
};

const buildProductInput = (
  product: ChatOrderProduct,
  items: SelectionItem[],
  delivery?: ProductDeliveryState
): ChatStoreProductInput => {
  const baseItem = items.find((i) => i.optionId === null);
  const optionItems = items.filter((i) => i.optionId !== null);

  return {
    productName: product.name,
    price: money(baseItem?.price ?? 0),
    quantity: baseItem?.quantity ?? 1,
    pictureId: product.thumbnail?._id ?? null,
    deliveryFee: getDeliveryFeeInput(product, delivery),
    options: getOptionsInput(product, optionItems),
  };
};

const groupBy = <T>(items: T[], keyFn: (item: T) => string): Record<string, T[]> =>
  items.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<string, T[]>
  );

export const buildOrderMutationInput = (
  state: SelectionState,
  products: ChatOrderProduct[]
): ChatStoreProductInput[] => {
  const itemsByProductId = groupBy(state.orderItems, (item) => item.productId);

  return products
    .filter((product) => itemsByProductId[product._id])
    .map((product) =>
      buildProductInput(product, itemsByProductId[product._id] ?? [], state.deliveryByProduct[product._id])
    );
};
